/**
 * Khoảng thời gian dùng chung cho nhóm màn số liệu (Tổng quan, Sử dụng AI).
 *
 * ── VÌ SAO KHÔNG DÙNG LẠI `LocNhatKy.moc` CỦA `loc.ts` ───────────────────
 * `loc.ts` là bộ lọc của màn Nhật ký thao tác: nó chở cả người, đơn vị, hành
 * động, mức độ — và nó sinh ra `tu` nhưng thường KHÔNG sinh `den` (mốc trên là
 * "bây giờ", không cần nói). Nhóm endpoint số liệu thì ngược lại: mọi phép gom
 * của audit-service bắt buộc có **cả hai mốc** và chặn khoảng quá 90 ngày (native
 * `app/luong_token.py::_buoc_khoang`, platform `module/LuongToken.java`), vì một `GROUP BY` không khoảng là quét
 * toàn bộ mọi partition.
 *
 * Nhét điều đó vào `LocNhatKy` sẽ làm một kiểu phục vụ hai hợp đồng ngược nhau,
 * và chỗ hỏng sẽ là màn nào đó gửi thiếu `den` rồi nhận 400 với câu lỗi nói về
 * một tham số mà người dùng không hề chọn.
 */

import { mocCuoi, mocDau } from './moc-chon.ts';

export const MOC_KHOANG = ['24_gio', '7_ngay', '30_ngay', 'thang_nay', 'tuy_chon'] as const;

export type MocKhoang = (typeof MOC_KHOANG)[number];

export interface KhoangAi {
  moc: MocKhoang;
  /** Chỉ dùng khi `moc === 'tuy_chon'`: `YYYY-MM-DDTHH:mm` hoặc `YYYY-MM-DD` — xem `moc-chon.ts`. */
  tu?: string;
  den?: string;
}

export const KHOANG_MAC_DINH: KhoangAi = { moc: '24_gio' };

/** Trần của audit-service cho mọi phép gom — `SO_NGAY_BO_LOC_TOI_DA`. */
export const TRAN_NGAY = 90;

const MOT_NGAY = 86_400_000;

function dauNgay(moc: number): number {
  const d = new Date(moc);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function dauThang(moc: number): number {
  const d = new Date(moc);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * Khoảng → hai mốc ISO tuyệt đối, **luôn đủ cả hai**, đã kẹp về 90 ngày.
 *
 * `neo` là tham số chứ không phải `Date.now()` bên trong, cùng lý do đã ghi ở
 * `loc.ts::khoangThoiGian`: hàm này chạy trong `queryKey`, nên lấy giờ hiện tại
 * ở đây là mỗi lần render một khoá mới và màn hình tải lại không dừng.
 *
 * Kẹp tại chỗ này chứ không để server trả 400: chọn một khoảng 6 tháng là việc
 * hợp lý của người dùng, và cái giá đúng của nó là "số liệu chỉ tính 90 ngày
 * gần nhất", không phải một màn trắng kèm lỗi không ai hiểu.
 */
export function thamSoKhoang(khoang: KhoangAi, neo: number): { tu: string; den: string } {
  let den = neo;
  let tu: number;
  if (khoang.moc === 'tuy_chon') {
    tu = khoang.tu ? mocDau(khoang.tu) : neo - MOT_NGAY;
    if (khoang.den) den = mocCuoi(khoang.den);
  } else if (khoang.moc === 'thang_nay') {
    tu = dauThang(neo);
  } else if (khoang.moc === '7_ngay') {
    tu = dauNgay(neo) - 6 * MOT_NGAY;
  } else if (khoang.moc === '30_ngay') {
    tu = dauNgay(neo) - 29 * MOT_NGAY;
  } else {
    tu = neo - MOT_NGAY;
  }
  // Người dùng chọn ngược (`tu` sau `den`) thì lùi một ngày, không ném: một ô
  // ngày chọn dở dang không được làm cả trang thành lỗi.
  if (Number.isNaN(tu) || tu >= den) tu = den - MOT_NGAY;
  const tran = TRAN_NGAY * MOT_NGAY;
  if (den - tu > tran) tu = den - tran;
  return { tu: new Date(tu).toISOString(), den: new Date(den).toISOString() };
}

/**
 * Bước gom của trục thời gian, suy từ độ dài khoảng.
 *
 * Suy ra chứ không cho người dùng chọn: một khoảng 24 giờ gom theo ngày ra đúng
 * một cột, và một khoảng 90 ngày gom theo giờ ra 2160 cột — cả hai đều là biểu
 * đồ không đọc được, và cả hai đều do một ô chọn mà người dùng không có lý do
 * nào để nghĩ về nó.
 */
export function buocThoiGian(tu: string, den: string): 'hour' | 'day' | 'month' {
  const gio = (Date.parse(den) - Date.parse(tu)) / 3_600_000;
  if (gio <= 48) return 'hour';
  if (gio <= 24 * 92) return 'day';
  return 'month';
}
